(() => {
  if (window.NerdcoreLootRunLoaded) return;
  window.NerdcoreLootRunLoaded = true;

  const WIDTH = 960;
  const HEIGHT = 540;
  const GROUND = 410;
  const PLAYER_X = 168;
  const JUMP_SPEED = 750;
  const GRAVITY = 2050;
  const MAX_RUN_SPEED = 720;
  // The visible pit is 632px wide; the longest possible jump is about 527px.
  const GAP_RAIL_WIDTH = 720;
  const LIME = '#c9ff00';
  const ORANGE = '#ff4d00';
  const PAPER = '#f4f0e8';
  const DARK = '#050505';
  const PIXEL_SCALE = 0.5;

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const padScore = (value) => String(Math.max(0, Math.floor(value))).padStart(6, '0');
  const intersects = (a, b) => (
    a.left < b.right &&
    a.right > b.left &&
    a.top < b.bottom &&
    a.bottom > b.top
  );

  const readBest = (key) => {
    try {
      const value = Number.parseInt(window.localStorage.getItem(key), 10);
      return Number.isFinite(value) && value > 0 ? value : 0;
    } catch (_error) {
      return 0;
    }
  };

  const writeBest = (key, value) => {
    try {
      window.localStorage.setItem(key, String(value));
    } catch (_error) {
      // Private browsing and strict cookie settings can disable local storage.
    }
  };

  const initGame = (root) => {
    if (!root || root.dataset.gameReady === 'true') return;
    root.dataset.gameReady = 'true';

    const canvas = root.querySelector('[data-game-canvas]');
    const context = canvas?.getContext('2d');
    const startOverlay = root.querySelector('[data-game-start]');
    const resultsOverlay = root.querySelector('[data-game-results]');
    const startButton = root.querySelector('[data-game-start-button]');
    const restartButton = root.querySelector('[data-game-restart-button]');
    const jumpButton = root.querySelector('[data-game-jump]');
    const soundButton = root.querySelector('[data-game-sound]');
    const scoreDisplay = root.querySelector('[data-game-score]');
    const bestDisplay = root.querySelector('[data-game-best]');
    const finalScoreDisplay = root.querySelector('[data-game-final-score]');
    const finalBestDisplay = root.querySelector('[data-game-final-best]');
    const resultLabel = root.querySelector('[data-game-result-label]');
    const liveRegion = root.querySelector('[data-game-live]');

    if (!canvas || !context || !startButton || !restartButton) {
      if (startButton) {
        startButton.disabled = true;
        startButton.textContent = 'Game unavailable';
      }
      return;
    }

    const storageKey = root.dataset.storageKey || 'nerdcore-loot-run-best-v1';
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    canvas.width = WIDTH * PIXEL_SCALE;
    canvas.height = HEIGHT * PIXEL_SCALE;

    const state = {
      status: 'ready',
      frameId: 0,
      lastTime: 0,
      elapsed: 0,
      distance: 0,
      score: 0,
      best: readBest(storageKey),
      speed: 330,
      spawnIn: 1.25,
      lootCount: 0,
      bonus: 0,
      soundEnabled: false,
      audioContext: null,
      jumpQueue: 0,
      trickLabel: '',
      trickTextTimer: 0,
      shake: 0,
      flash: 0,
      reducedMotion: reducedMotionQuery.matches,
      objects: [],
      particles: [],
      player: {
        y: 0,
        velocityY: 0,
        grounded: true,
        grinding: false,
        rail: null,
        wheelSpin: 0,
        flipUsed: false,
        flipping: false,
        flipTime: 0,
      },
    };

    const setTransform = () => {
      context.setTransform(PIXEL_SCALE, 0, 0, PIXEL_SCALE, 0, 0);
      context.imageSmoothingEnabled = false;
    };

    const updateDisplays = () => {
      if (scoreDisplay) scoreDisplay.textContent = padScore(state.score);
      if (bestDisplay) bestDisplay.textContent = padScore(Math.max(state.best, state.score));
    };

    const announce = (message) => {
      if (!liveRegion) return;
      liveRegion.textContent = '';
      window.setTimeout(() => {
        liveRegion.textContent = message;
      }, 20);
    };

    const ensureAudio = () => {
      if (!state.soundEnabled) return null;
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      if (!AudioContext) return null;
      if (!state.audioContext) state.audioContext = new AudioContext();
      if (state.audioContext.state === 'suspended') state.audioContext.resume();
      return state.audioContext;
    };

    const tone = (frequency, duration, type = 'square', slide = 0) => {
      const audio = ensureAudio();
      if (!audio) return;
      const oscillator = audio.createOscillator();
      const gain = audio.createGain();
      const now = audio.currentTime;
      oscillator.type = type;
      oscillator.frequency.setValueAtTime(frequency, now);
      oscillator.frequency.linearRampToValueAtTime(Math.max(40, frequency + slide), now + duration);
      gain.gain.setValueAtTime(0.055, now);
      gain.gain.exponentialRampToValueAtTime(0.001, now + duration);
      oscillator.connect(gain);
      gain.connect(audio.destination);
      oscillator.start(now);
      oscillator.stop(now + duration);
    };

    const sound = (name) => {
      if (name === 'jump') tone(175, 0.12, 'square', 150);
      if (name === 'loot') {
        tone(520, 0.08, 'square', 160);
        window.setTimeout(() => tone(720, 0.07, 'square', 100), 55);
      }
      if (name === 'grind') tone(105, 0.11, 'sawtooth', 45);
      if (name === 'trick') {
        tone(310, 0.09, 'square', 210);
        window.setTimeout(() => tone(610, 0.1, 'square', 130), 70);
      }
      if (name === 'crash') tone(155, 0.3, 'sawtooth', -100);
      if (name === 'start') tone(260, 0.1, 'square', 120);
    };

    const playerRect = () => {
      const bottom = GROUND + state.player.y - 6;
      const height = 152;
      return {
        left: PLAYER_X - 20,
        right: PLAYER_X + 24,
        top: bottom - height,
        bottom,
      };
    };

    const makeParticle = (x, y, color, force = 1) => ({
      x,
      y,
      velocityX: (Math.random() - 0.5) * 190 * force,
      velocityY: (-80 - Math.random() * 160) * force,
      life: 0.35 + Math.random() * 0.4,
      maxLife: 0.75,
      size: 2 + Math.random() * 5,
      color,
    });

    const burst = (x, y, color, count) => {
      if (state.reducedMotion) return;
      for (let index = 0; index < count; index += 1) {
        state.particles.push(makeParticle(x, y, color));
      }
    };

    const addLootTrail = (x, baseY, count = 3) => {
      const kinds = ['shirt', 'controller', 'deck'];
      for (let index = 0; index < count; index += 1) {
        state.objects.push({
          kind: 'loot',
          lootType: kinds[Math.floor(Math.random() * kinds.length)],
          x: x + index * 56,
          y: baseY - Math.sin((index / Math.max(1, count - 1)) * Math.PI) * 34,
          radius: 19,
          spin: Math.random() * Math.PI,
          collected: false,
        });
      }
    };

    const spawnSet = () => {
      const x = WIDTH + 80;
      const roll = Math.random();
      let obstacle;

      if (state.elapsed > 18 && roll < 0.22) {
        obstacle = {
          kind: 'obstacle',
          type: 'gapRail',
          x,
          y: GROUND - 72,
          width: GAP_RAIL_WIDTH,
          height: 12,
          completed: false,
        };
        addLootTrail(x + 100, GROUND - 108, 9);
      } else if (state.elapsed > 8 && roll < 0.42) {
        obstacle = { kind: 'obstacle', type: 'rail', x, y: GROUND - 72, width: 158, height: 12 };
        addLootTrail(x + 25, GROUND - 150, 3);
      } else if (roll < 0.72) {
        obstacle = { kind: 'obstacle', type: 'cone', x, y: GROUND - 56, width: 42, height: 56 };
        addLootTrail(x + 2, GROUND - 128, 2);
      } else {
        obstacle = { kind: 'obstacle', type: 'hydrant', x, y: GROUND - 76, width: 52, height: 76 };
        addLootTrail(x - 2, GROUND - 148, 2);
      }

      state.objects.push(obstacle);
      const pace = clamp(1.75 - state.elapsed * 0.008, 0.92, 1.75);
      const normalDelay = pace + Math.random() * 0.75;
      const gapClearanceDelay = obstacle.type === 'gapRail'
        ? (obstacle.width + 220) / state.speed
        : 0;
      state.spawnIn = Math.max(normalDelay, gapClearanceDelay);
    };

    const resetState = () => {
      state.elapsed = 0;
      state.distance = 0;
      state.score = 0;
      state.speed = 330;
      state.spawnIn = 1.3;
      state.lootCount = 0;
      state.bonus = 0;
      state.jumpQueue = 0;
      state.trickLabel = '';
      state.trickTextTimer = 0;
      state.shake = 0;
      state.flash = 0;
      state.objects = [];
      state.particles = [];
      state.player.y = 0;
      state.player.velocityY = 0;
      state.player.grounded = true;
      state.player.grinding = false;
      state.player.rail = null;
      state.player.wheelSpin = 0;
      state.player.flipUsed = false;
      state.player.flipping = false;
      state.player.flipTime = 0;
      updateDisplays();
    };

    const startGame = () => {
      window.cancelAnimationFrame(state.frameId);
      resetState();
      state.status = 'running';
      state.lastTime = window.performance.now();
      startOverlay.hidden = true;
      resultsOverlay.hidden = true;
      sound('start');
      announce('Loot Run started.');
      state.frameId = window.requestAnimationFrame(loop);
    };

    const endGame = () => {
      if (state.status !== 'running') return;
      state.score = Math.floor(state.distance * 0.04 + state.bonus);
      state.status = 'over';
      state.shake = state.reducedMotion ? 0 : 0.32;
      burst(PLAYER_X, GROUND + state.player.y - 30, ORANGE, 14);
      sound('crash');

      const isNewBest = state.score > state.best;
      if (isNewBest) {
        state.best = state.score;
        writeBest(storageKey, state.best);
      }

      updateDisplays();
      if (finalScoreDisplay) finalScoreDisplay.textContent = padScore(state.score);
      if (finalBestDisplay) finalBestDisplay.textContent = padScore(state.best);
      if (resultLabel) resultLabel.textContent = isNewBest ? 'New personal best' : 'Run over';
      resultsOverlay.hidden = false;
      announce(`${isNewBest ? 'New personal best.' : 'Run over.'} Score ${state.score}.`);
      draw();
      window.setTimeout(() => restartButton.focus(), 80);
    };

    const queueJump = () => {
      if (state.status !== 'running') return;
      state.jumpQueue = Math.min(2, state.jumpQueue + 1);
    };

    const awardTrick = (label, points) => {
      state.bonus += points;
      state.trickLabel = `${label} +${points}`;
      state.trickTextTimer = 0.9;
    };

    const updatePlayer = (delta) => {
      const player = state.player;
      const previousBottom = GROUND + player.y;

      while (state.jumpQueue > 0) {
        if (player.grounded) {
          player.velocityY = -JUMP_SPEED;
          player.grounded = false;
          player.grinding = false;
          player.rail = null;
          player.flipUsed = false;
          sound('jump');
          burst(PLAYER_X - 14, previousBottom, PAPER, 5);
        } else if (!player.flipUsed && !player.grinding) {
          player.flipUsed = true;
          player.flipping = true;
          player.flipTime = 0;
          awardTrick('KICKFLIP', 150);
          sound('trick');
          burst(PLAYER_X, GROUND + player.y - 4, LIME, 7);
          announce('Kickflip. 150 points.');
        }
        state.jumpQueue -= 1;
      }

      if (player.flipping) {
        player.flipTime += delta;
        if (player.flipTime >= 0.52) {
          player.flipping = false;
          player.flipTime = 0;
        }
      }

      if (player.grinding && player.rail) {
        const rail = player.rail;
        const stillOnRail = PLAYER_X + 23 > rail.x && PLAYER_X - 20 < rail.x + rail.width;
        if (stillOnRail) {
          player.y = rail.y - GROUND;
          player.velocityY = 0;
          player.grounded = true;
          state.bonus += delta * 32;
          if (!state.reducedMotion && Math.random() < delta * 18) {
            state.particles.push(makeParticle(PLAYER_X - 18, rail.y + 4, ORANGE, 0.55));
          }
        } else {
          if (rail.type === 'gapRail' && !rail.completed) {
            rail.completed = true;
            awardTrick('GAP GRIND', 250);
            sound('trick');
            announce('Gap grind complete. 250 points.');
          }
          player.grinding = false;
          player.rail = null;
          player.grounded = false;
          player.velocityY = 70;
          player.flipUsed = true;
        }
      } else {
        player.velocityY += GRAVITY * delta;
        player.y += player.velocityY * delta;

        if (player.y >= 0) {
          if (!player.grounded && player.velocityY > 220) burst(PLAYER_X - 12, GROUND, PAPER, 4);
          player.y = 0;
          player.velocityY = 0;
          player.grounded = true;
          player.flipUsed = false;
          player.flipping = false;
          player.flipTime = 0;
        } else {
          player.grounded = false;
        }

        if (player.velocityY >= 0 && player.y < 0) {
          const boardBottom = GROUND + player.y;
          const rail = state.objects.find((object) => (
            object.kind === 'obstacle' &&
            (object.type === 'rail' || object.type === 'gapRail') &&
            PLAYER_X + 23 > object.x &&
            PLAYER_X - 20 < object.x + object.width &&
            previousBottom <= object.y + 18 &&
            boardBottom >= object.y - 20
          ));

          if (rail) {
            player.y = rail.y - GROUND;
            player.velocityY = 0;
            player.grounded = true;
            player.grinding = true;
            player.rail = rail;
            player.flipping = false;
            player.flipTime = 0;
            state.bonus += 75;
            sound('grind');
          }
        }
      }

      player.wheelSpin += state.speed * delta * 0.08;
    };

    const updateObjects = (delta) => {
      const playerBox = playerRect();

      state.objects.forEach((object) => {
        object.x -= state.speed * delta;
        if (object.kind === 'loot') {
          object.spin += delta * 4.5;
          const lootBox = {
            left: object.x - object.radius,
            right: object.x + object.radius,
            top: object.y - object.radius,
            bottom: object.y + object.radius,
          };

          if (!object.collected && intersects(playerBox, lootBox)) {
            object.collected = true;
            state.lootCount += 1;
            state.bonus += 100;
            state.flash = state.reducedMotion ? 0 : 0.12;
            burst(object.x, object.y, LIME, 9);
            sound('loot');
          }
          return;
        }

        if (object.type === 'gapRail') {
          const gapLeft = object.x + 56;
          const gapRight = object.x + object.width - 32;
          const overGap = (
            PLAYER_X + 12 > gapLeft &&
            PLAYER_X - 12 < gapRight
          );
          const boardBottom = GROUND + state.player.y;
          const reachedPitLevel = boardBottom >= GROUND - 4;

          if (overGap && state.player.rail !== object && reachedPitLevel) endGame();
          return;
        }

        if (object.type === 'rail') return;
        const forgiveness = 6;
        const obstacleBox = {
          left: object.x + forgiveness,
          right: object.x + object.width - forgiveness,
          top: object.y + forgiveness,
          bottom: object.y + object.height,
        };

        if (intersects(playerBox, obstacleBox)) endGame();
      });

      state.objects = state.objects.filter((object) => {
        if (object.collected) return false;
        const width = object.width || object.radius * 2;
        return object.x + width > -80;
      });
    };

    const updateParticles = (delta) => {
      state.particles.forEach((particle) => {
        particle.life -= delta;
        particle.velocityY += 460 * delta;
        particle.x += particle.velocityX * delta;
        particle.y += particle.velocityY * delta;
      });
      state.particles = state.particles.filter((particle) => particle.life > 0);
    };

    const update = (delta) => {
      state.elapsed += delta;
      state.speed = clamp(330 + state.elapsed * 8.5, 330, MAX_RUN_SPEED);
      state.distance += state.speed * delta;
      state.spawnIn -= delta;
      if (state.spawnIn <= 0) spawnSet();

      updatePlayer(delta);
      updateObjects(delta);
      updateParticles(delta);

      state.flash = Math.max(0, state.flash - delta);
      state.shake = Math.max(0, state.shake - delta);
      state.trickTextTimer = Math.max(0, state.trickTextTimer - delta);
      state.score = Math.floor(state.distance * 0.04 + state.bonus);
      updateDisplays();
    };

    const pixelRect = (x, y, width, height, color) => {
      const grid = 4;
      context.fillStyle = color;
      context.fillRect(
        Math.round(x / grid) * grid,
        Math.round(y / grid) * grid,
        Math.max(grid, Math.round(width / grid) * grid),
        Math.max(grid, Math.round(height / grid) * grid),
      );
    };

    const drawPixelLimb = (startX, startY, endX, endY, color, size = 10) => {
      const distance = Math.hypot(endX - startX, endY - startY);
      const steps = Math.max(1, Math.ceil(distance / 6));
      for (let step = 0; step <= steps; step += 1) {
        const amount = step / steps;
        pixelRect(
          startX + (endX - startX) * amount - size / 2,
          startY + (endY - startY) * amount - size / 2,
          size,
          size,
          color,
        );
      }
    };

    const drawBackground = () => {
      pixelRect(0, 0, WIDTH, HEIGHT, '#090b12');
      pixelRect(0, 188, WIDTH, GROUND - 188, '#11131d');
      pixelRect(0, 300, WIDTH, GROUND - 300, '#17141c');

      // A deliberately blocky moon and sparse stars keep the scene readable.
      pixelRect(752, 52, 80, 8, '#c4d69b');
      pixelRect(740, 60, 104, 56, '#c4d69b');
      pixelRect(748, 116, 88, 8, '#c4d69b');
      pixelRect(756, 68, 16, 12, '#9cab7e');
      pixelRect(808, 92, 20, 12, '#9cab7e');

      for (let index = 0; index < 18; index += 1) {
        const x = (index * 157 + 44) % WIDTH;
        const y = 32 + ((index * 71) % 150);
        pixelRect(x, y, index % 4 === 0 ? 8 : 4, 4, index % 5 === 0 ? LIME : '#777a80');
      }

      const farOffset = Math.floor(((state.distance * 0.022) % 96) / 4) * 4;
      for (let index = -2; index < 13; index += 1) {
        const x = index * 96 - farOffset;
        const buildingHeight = 76 + (((index + 20) * 44) % 124);
        const top = GROUND - buildingHeight;
        pixelRect(x, top, 84, buildingHeight, index % 3 === 0 ? '#161b23' : '#131720');
        if (index % 3 === 0) pixelRect(x + 36, top - 24, 8, 24, '#161b23');
        for (let row = 0; row < 4; row += 1) {
          for (let column = 0; column < 3; column += 1) {
            if ((row + column + index) % 3 === 0) {
              pixelRect(x + 12 + column * 24, top + 20 + row * 28, 8, 4, '#75311f');
            }
          }
        }
      }

      const nearOffset = Math.floor(((state.distance * 0.08) % 168) / 4) * 4;
      for (let x = -168 - nearOffset; x < WIDTH + 168; x += 168) {
        pixelRect(x, GROUND - 64, 112, 64, '#0c0f13');
        pixelRect(x + 20, GROUND - 84, 56, 20, '#0c0f13');
        pixelRect(x + 128, GROUND - 44, 32, 44, '#0c0f13');
      }

      pixelRect(0, GROUND, WIDTH, HEIGHT - GROUND, '#080909');
      pixelRect(0, GROUND, WIDTH, 8, ORANGE);
      pixelRect(0, GROUND + 56, WIDTH, 4, '#222425');

      const roadOffset = Math.floor(((state.distance * 0.7) % 132) / 4) * 4;
      for (let x = -132 - roadOffset; x < WIDTH + 132; x += 132) {
        pixelRect(x, GROUND + 88, 68, 4, '#728238');
      }
    };

    const drawCone = (object) => {
      const x = object.x;
      const y = object.y;
      pixelRect(x + 16, y, 12, 8, ORANGE);
      pixelRect(x + 12, y + 8, 20, 12, ORANGE);
      pixelRect(x + 8, y + 20, 28, 12, ORANGE);
      pixelRect(x + 8, y + 28, 28, 8, PAPER);
      pixelRect(x + 4, y + 36, 36, 12, ORANGE);
      pixelRect(x, y + 48, 44, 8, '#1d2022');
    };

    const drawHydrant = (object) => {
      const x = object.x;
      const y = object.y;
      pixelRect(x + 16, y, 24, 8, '#d53d0a');
      pixelRect(x + 8, y + 8, 40, 12, '#d53d0a');
      pixelRect(x + 12, y + 20, 32, 52, '#d53d0a');
      pixelRect(x, y + 28, 52, 20, '#d53d0a');
      pixelRect(x + 4, y + 32, 44, 8, PAPER);
      pixelRect(x + 8, y + 68, 40, 8, '#25282a');
    };

    const drawRail = (object) => {
      pixelRect(object.x, object.y, object.width, 8, '#c5c3bc');
      pixelRect(object.x + 16, object.y + 8, 8, GROUND - object.y - 8, '#7d8082');
      pixelRect(object.x + object.width - 24, object.y + 8, 8, GROUND - object.y - 8, '#7d8082');
      pixelRect(object.x - 4, object.y - 4, object.width + 8, 4, PAPER);
    };

    const drawGapRail = (object) => {
      const gapX = object.x + 52;
      const gapWidth = object.width - 80;
      pixelRect(gapX, GROUND - 4, gapWidth, HEIGHT - GROUND + 8, '#010202');
      pixelRect(gapX - 8, GROUND - 4, 8, HEIGHT - GROUND + 8, ORANGE);
      pixelRect(gapX + gapWidth, GROUND - 4, 8, HEIGHT - GROUND + 8, ORANGE);
      for (let x = gapX + 12; x < gapX + gapWidth - 12; x += 36) {
        pixelRect(x, GROUND + 28, 16, 8, '#46301f');
      }
      drawRail(object);
    };

    const drawLoot = (object) => {
      const fullWidth = object.lootType === 'deck' ? 48 : 40;
      const flipWidth = Math.max(8, Math.round((0.3 + Math.abs(Math.cos(object.spin)) * 0.7) * fullWidth / 4) * 4);
      const x = object.x - flipWidth / 2;
      const y = object.y;
      pixelRect(x - 4, y - 24, flipWidth + 8, 48, '#243000');

      if (object.lootType === 'shirt') {
        pixelRect(x, y - 12, flipWidth, 12, LIME);
        pixelRect(x + 8, y - 16, flipWidth - 16, 36, LIME);
        if (flipWidth > 28) {
          const center = x + flipWidth / 2;
          pixelRect(center - 8, y - 8, 4, 20, DARK);
          pixelRect(center + 4, y - 8, 4, 20, DARK);
          pixelRect(center - 4, y - 4, 4, 8, DARK);
          pixelRect(center, y, 4, 8, DARK);
        }
      } else if (object.lootType === 'controller') {
        pixelRect(x, y - 12, flipWidth, 24, LIME);
        pixelRect(x + 4, y + 8, 8, 12, LIME);
        pixelRect(x + flipWidth - 12, y + 8, 8, 12, LIME);
        if (flipWidth > 28) {
          pixelRect(x + 8, y - 4, 12, 4, DARK);
          pixelRect(x + 12, y - 8, 4, 12, DARK);
          pixelRect(x + flipWidth - 16, y - 8, 4, 4, ORANGE);
          pixelRect(x + flipWidth - 12, y, 4, 4, ORANGE);
        }
      } else {
        pixelRect(x, y - 8, flipWidth, 12, LIME);
        pixelRect(x + 4, y - 12, flipWidth - 8, 4, PAPER);
        if (flipWidth > 28) {
          pixelRect(x + flipWidth / 2 - 8, y - 8, 16, 4, ORANGE);
          pixelRect(x + 8, y + 4, 8, 8, '#aeb3b4');
          pixelRect(x + flipWidth - 16, y + 4, 8, 8, '#aeb3b4');
          pixelRect(x + 12, y + 8, 4, 4, DARK);
          pixelRect(x + flipWidth - 12, y + 8, 4, 4, DARK);
        }
      }
    };

    const drawObjects = () => {
      state.objects.forEach((object) => {
        if (object.kind === 'loot') {
          drawLoot(object);
        } else if (object.type === 'cone') {
          drawCone(object);
        } else if (object.type === 'hydrant') {
          drawHydrant(object);
        } else if (object.type === 'rail') {
          drawRail(object);
        } else if (object.type === 'gapRail') {
          drawGapRail(object);
        }
      });
    };

    const drawSkater = () => {
      const player = state.player;
      const boardY = GROUND + player.y;
      const animationFrame = Math.floor(player.wheelSpin / 8) % 2;
      const isAirborne = !player.grounded;
      const hip = { x: 0, y: -58 };
      let backKnee = { x: -18, y: -38 };
      let backFoot = { x: -25, y: -13 };
      let frontKnee = { x: 21, y: -38 };
      let frontFoot = { x: 27, y: -13 };

      if (isAirborne) {
        backKnee = { x: -25, y: -45 };
        backFoot = { x: -17, y: -25 };
        frontKnee = { x: 26, y: -49 };
        frontFoot = { x: 20, y: -25 };
        if (player.flipping) {
          backKnee.y -= 8;
          frontKnee.y -= 8;
          backFoot.y -= 5;
          frontFoot.y -= 5;
        }
      } else if (animationFrame === 1) {
        backKnee.x -= 5;
        frontKnee.x += 4;
      }

      context.save();
      context.translate(PLAYER_X, boardY);

      if (player.grounded && !player.grinding) {
        pixelRect(-38, 5, 80, 4, '#151719');
      }

      // Legs use visible square joints so the pose reads at a glance.
      drawPixelLimb(hip.x - 8, hip.y, backKnee.x, backKnee.y, '#090a0c', 16);
      drawPixelLimb(backKnee.x, backKnee.y, backFoot.x, backFoot.y, '#090a0c', 16);
      drawPixelLimb(hip.x - 8, hip.y, backKnee.x, backKnee.y, '#d8d3c9', 8);
      drawPixelLimb(backKnee.x, backKnee.y, backFoot.x, backFoot.y, '#d8d3c9', 8);
      pixelRect(backFoot.x - 8, backFoot.y - 4, 24, 8, '#24272a');

      drawPixelLimb(hip.x + 8, hip.y, frontKnee.x, frontKnee.y, '#090a0c', 16);
      drawPixelLimb(frontKnee.x, frontKnee.y, frontFoot.x, frontFoot.y, '#090a0c', 16);
      drawPixelLimb(hip.x + 8, hip.y, frontKnee.x, frontKnee.y, PAPER, 8);
      drawPixelLimb(frontKnee.x, frontKnee.y, frontFoot.x, frontFoot.y, PAPER, 8);
      pixelRect(frontFoot.x - 8, frontFoot.y - 4, 24, 8, '#24272a');

      const bodyY = -112;
      const shoulderY = bodyY + 15;
      pixelRect(-24, bodyY - 4, 56, 60, '#08090b');
      pixelRect(-20, bodyY, 48, 52, '#272a2d');
      pixelRect(-20, bodyY, 12, 52, '#1c1f22');
      pixelRect(-8, bodyY + 12, 4, 24, LIME);
      pixelRect(8, bodyY + 12, 4, 24, LIME);
      pixelRect(-4, bodyY + 16, 4, 8, LIME);
      pixelRect(0, bodyY + 20, 4, 8, LIME);
      pixelRect(4, bodyY + 24, 4, 8, LIME);
      pixelRect(-20, bodyY + 44, 48, 8, ORANGE);

      const rearHand = { x: -43, y: -79 };
      const frontHand = { x: 47, y: -82 };
      drawPixelLimb(-16, shoulderY, rearHand.x, rearHand.y, '#090a0c', 16);
      drawPixelLimb(-16, shoulderY, rearHand.x, rearHand.y, '#34373a', 8);
      drawPixelLimb(24, shoulderY, frontHand.x, frontHand.y, '#090a0c', 16);
      drawPixelLimb(24, shoulderY, frontHand.x, frontHand.y, '#34373a', 8);
      pixelRect(rearHand.x - 4, rearHand.y - 4, 8, 8, '#d5a888');
      pixelRect(frontHand.x - 4, frontHand.y - 4, 8, 8, '#d5a888');

      // Square head, hair, and cap avoid the uncanny vector face from the first pass.
      pixelRect(0, bodyY - 40, 36, 36, '#08090b');
      pixelRect(4, bodyY - 36, 28, 32, '#d5a888');
      pixelRect(4, bodyY - 40, 32, 12, '#16181b');
      pixelRect(-4, bodyY - 36, 12, 20, '#16181b');
      pixelRect(0, bodyY - 44, 40, 8, ORANGE);
      pixelRect(32, bodyY - 40, 16, 4, ORANGE);
      pixelRect(24, bodyY - 24, 4, 4, DARK);
      pixelRect(32, bodyY - 12, 8, 4, '#b97b60');

      // The second input flips the deck around its long axis once per jump.
      const flipProgress = player.flipping ? clamp(player.flipTime / 0.52, 0, 1) : 0;
      const flipVisibility = player.flipping ? Math.abs(Math.cos(flipProgress * Math.PI * 2)) : 1;
      const deckHeight = Math.max(4, Math.round((12 * flipVisibility) / 4) * 4);
      const deckOffset = player.flipping ? -8 * Math.sin(flipProgress * Math.PI) : 0;
      const deckColor = player.flipping && flipProgress > 0.25 && flipProgress < 0.75 ? LIME : ORANGE;
      pixelRect(-44, -12 + deckOffset, 88, deckHeight, DARK);
      pixelRect(-40, -8 + deckOffset, 80, Math.max(4, deckHeight - 4), deckColor);
      pixelRect(-44, -8 + deckOffset, 8, 4, PAPER);
      pixelRect(40, -8 + deckOffset, 8, 4, PAPER);
      if (deckHeight >= 8) {
        pixelRect(-32, deckOffset, 12, 8, '#b8bdbe');
        pixelRect(24, deckOffset, 12, 8, '#b8bdbe');
        pixelRect(-28, deckOffset, 4, 8, DARK);
        pixelRect(28, deckOffset, 4, 8, DARK);
      }

      context.restore();
    };

    const drawParticles = () => {
      state.particles.forEach((particle) => {
        context.globalAlpha = clamp(particle.life / particle.maxLife, 0, 1);
        context.fillStyle = particle.color;
        context.fillRect(particle.x, particle.y, particle.size, particle.size);
      });
      context.globalAlpha = 1;
    };

    const drawHud = () => {
      context.fillStyle = 'rgba(5, 5, 5, 0.72)';
      context.fillRect(20, 20, 166, 39);
      context.fillStyle = LIME;
      context.font = '900 18px "Courier New", monospace';
      context.fillText(`LOOT x ${String(state.lootCount).padStart(2, '0')}`, 34, 46);

      if (state.trickTextTimer > 0) {
        context.fillStyle = LIME;
        context.font = '900 24px "Courier New", monospace';
        context.textAlign = 'center';
        context.fillText(state.trickLabel, PLAYER_X + 16, GROUND + state.player.y - 176);
        context.textAlign = 'start';
      }
    };

    const draw = () => {
      setTransform();
      context.clearRect(0, 0, WIDTH, HEIGHT);
      context.save();
      if (state.shake > 0 && !state.reducedMotion) {
        context.translate((Math.random() - 0.5) * 10, (Math.random() - 0.5) * 7);
      }
      drawBackground();
      drawObjects();
      drawSkater();
      drawParticles();
      drawHud();
      context.restore();

      if (state.flash > 0) {
        context.fillStyle = `rgba(201, 255, 0, ${state.flash * 1.7})`;
        context.fillRect(0, 0, WIDTH, HEIGHT);
      }
    };

    const loop = (time) => {
      if (state.status !== 'running') return;
      const delta = clamp((time - state.lastTime) / 1000, 0, 0.034);
      state.lastTime = time;
      update(delta);
      draw();
      if (state.status === 'running') state.frameId = window.requestAnimationFrame(loop);
    };

    const onKeyDown = (event) => {
      if (state.status !== 'running') return;
      if (event.code === 'Space' || event.code === 'ArrowUp' || event.code === 'KeyW') {
        event.preventDefault();
        if (!event.repeat) queueJump();
      }
    };

    const onCanvasPointer = (event) => {
      if (state.status !== 'running') return;
      event.preventDefault();
      queueJump();
    };

    const onVisibilityChange = () => {
      if (!document.hidden && state.status === 'running') state.lastTime = window.performance.now();
    };

    const onMotionPreference = (event) => {
      state.reducedMotion = event.matches;
      if (event.matches) state.particles = [];
    };

    const bindHoldButton = (button, press, release) => {
      if (!button) return () => {};
      const onPress = (event) => {
        event.preventDefault();
        press();
      };
      const onRelease = (event) => {
        event.preventDefault();
        release();
      };
      button.addEventListener('pointerdown', onPress);
      button.addEventListener('pointerup', onRelease);
      button.addEventListener('pointercancel', onRelease);
      button.addEventListener('pointerleave', onRelease);
      return () => {
        button.removeEventListener('pointerdown', onPress);
        button.removeEventListener('pointerup', onRelease);
        button.removeEventListener('pointercancel', onRelease);
        button.removeEventListener('pointerleave', onRelease);
      };
    };

    const stopJumpHold = bindHoldButton(jumpButton, queueJump, () => {});

    startButton.addEventListener('click', startGame);
    restartButton.addEventListener('click', startGame);
    canvas.addEventListener('pointerdown', onCanvasPointer);
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('visibilitychange', onVisibilityChange);
    reducedMotionQuery.addEventListener?.('change', onMotionPreference);

    soundButton?.addEventListener('click', () => {
      state.soundEnabled = !state.soundEnabled;
      soundButton.setAttribute('aria-pressed', String(state.soundEnabled));
      soundButton.setAttribute('aria-label', `Turn game sound ${state.soundEnabled ? 'off' : 'on'}`);
      soundButton.textContent = state.soundEnabled ? 'Sound on' : 'Sound off';
      if (state.soundEnabled) sound('start');
    });

    root._lootRunDestroy = () => {
      window.cancelAnimationFrame(state.frameId);
      stopJumpHold();
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      reducedMotionQuery.removeEventListener?.('change', onMotionPreference);
      state.audioContext?.close();
    };

    updateDisplays();
    draw();
  };

  const initializeAll = (scope = document) => {
    scope.querySelectorAll('[data-loot-run]').forEach(initGame);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll());
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    event.target.querySelectorAll('[data-loot-run]').forEach((root) => root._lootRunDestroy?.());
  });
})();
